import PageWrapper from "../../components/PageWrapper";
import { useTranslation } from "react-i18next";
import PDFViewer from "../../components/PDF";
import { useContext, useEffect, useMemo, useState } from "react";
import SectionDivider from "../../components/SectionDivider";
import CustomButton from "../../components/CustomButton";
import { useParams } from "react-router-dom";
import CustomInput from "../../components/CustomInput";
import InformationBox from "../../components/InformationBox";
import AreYouSureModal from "../../components/AreYouSureModal";
import TextDiv from "../../components/TextDiv";
import StatusBadge from "../../components/StatusBadge";
import {
  Radio,
  RadioGroup,
  RadioGroupItem,
  RadioLabel,
} from "../../components/Radio";
import LoaderWithProgress from "../../components/LoaderWithProgress";
import axiosClient from "../../services/axiosClient";
import { formatDate } from "../../utils/toDate";
import { getFileType, sortByCustomOrder } from "../../utils/files";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import { UserAuth } from "../../utils/AuthContext";
import { getCurrency } from "../../utils/currency";
import ErrorMessage from "../../components/ErrorMessage";
import useLanguage from "../../utils/useLanguage";
import CommonRejectionsReasons from "../../components/CommonRejectionsReasons";
import ConfirmationBox from "../../components/ConfirmationBox";

const INVOICE_FILES_TYPES = [
  "invoice",
  "supplierCertificate",
  "articlesOfAssociation",
  "BaghdadTransactionRequest",
];

const orderedKeys = [
  "BaghdadTransactionRequest",
  "BaghdadPaymentDetails",
  "BaghdadConfirmation",
  "BaghdadEntryRequest",
  "BaghdadPromise",
  "BankPledge",
];

const invoiceOrderFiles = [
  "invoice",
  "supplierCertificate",
  "articlesOfAssociation",
  "BaghdadTransactionRequest",
];

const BanksInvoicesApplicationPaymentDetails = () => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const [paymentDocs, setPaymentDocs] = useState([]);
  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const { paymentId, id } = useParams();
  const [payment, setPayment] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [paymentEditableFields, setPaymentEditableFields] = useState({
    notes: "",
    currentUserStatus: undefined,
  });
  const [sentConfirmationModal, setSentConfirmationModal] = useState(false);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [updatingPaymentError, setUpdatingPaymentError] = useState({
    selecteError: "",
    rejectionReasonError: "",
  });
  const { userData } = useContext(UserAuth);
  const [isFetchingInvoiceFiles, setIsFetchingInvoiceFiles] = useState(false);
  const [preventEditing, setPreventEditing] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [rejectionReason, setRejectionReason] = useState("");
  const [transNum, setTransNum] = useState("");
  const [loadingFilesDownload, setLoadingFilesDownload] = useState(false);
  const [loadingFilesPrinting, setLoadingFilesPrinting] = useState(false);
  const [loadingFilesEmail, setLoadingFilesEmail] = useState(false);
  const [filesEmailSuccess, setFilesEmailSuccess] = useState("");
  const [filesEmailError, setFilesEmailError] = useState("");

  const [showSendToPolicySureModal, setShowSendToPolicySureModal] =
    useState(false);
  const [showMarkAsSentToPolicies, setMarkAsSentToPolicies] = useState(false);

  const [executeLoading, setExecuteLoading] = useState(false);

  const userRole = userData.role;

  const filesTitles = useMemo(
    () => ({
      invoice: t("invoice"),
      supplierCertificate: t("supplierCertificate"),
      articlesOfAssociation: t("articlesOfAssociation"),
      BaghdadConfirmation: t("confirmDocument"),
      BaghdadEntryRequest: t("transactionRequest"),
      BaghdadPaymentDetails: t("inforamtionRequest"),
      BankPledge: t("bankPledge"),
      BaghdadPromise: t("declarationAndUndertaking"),
      BaghdadTransactionRequest: t("financialTransferRequestForm"),
      customsDeclaration: t("customsDeclaration"),
      thirdPartyContract: t("thirdPartyContract"),
      articleOfAssociation: t("articleOfAssociation"),
    }),
    [t],
  );

  const fetchPayment = async () => {
    try {
      setLoadingPayment(true);
      setPaymentError("");
      const response = await axiosClient.get(
        `/multiCInvoicePaymentDetails/${paymentId}`,
      );

      setPayment(response.data);
      const isCurrentUserCreator =
        response.data.creatorId === parseInt(userData.userId);
      const isCurrentUserAuditor =
        response.data.auditorId === parseInt(userData.userId);
      const isCurrentUserExecutor =
        response.data.executorId === parseInt(userData.userId);

      let currentUserStatus;
      if (isCurrentUserCreator) {
        const creatorStatus = response.data?.creatorStatus;
        currentUserStatus =
          creatorStatus === "ACCEPTED"
            ? "ACCEPTED"
            : creatorStatus === "REJECTED"
              ? "REJECTED"
              : null;
      } else if (isCurrentUserAuditor) {
        const auditorStatus = response.data?.auditorStatus;
        currentUserStatus =
          auditorStatus === "ACCEPTED"
            ? "ACCEPTED"
            : auditorStatus === "REJECTED"
              ? "REJECTED"
              : null;
      } else if (isCurrentUserExecutor) {
        const executorStatus = response.data?.executorStatus;
        currentUserStatus =
          executorStatus === "EXECUTED"
            ? "EXECUTED"
            : executorStatus === "REJECTED"
              ? "REJECTED"
              : null;
      }

      setPaymentEditableFields({
        notes: response.data.notes,
        currentUserStatus,
      });

      setPreventEditing(() => {
        const isCurrentUserCreator =
          response.data.creatorId === parseInt(userData.userId);
        const isCurrentUserAuditor =
          response.data.auditorId === parseInt(userData.userId);
        const isCurrentUserExecutor =
          response.data.executorId === parseInt(userData.userId);

        // Check if user is a creator role
        if (userData.role === "applicationsCreator") {
          // If no creator assigned yet, allow any creator to edit
          if (response.data.creatorId === null) {
            return false; // Allow editing
          }
          // If creator is assigned, only allow that specific creator to edit
          if (isCurrentUserCreator && response.data.status !== "ACCEPTED") {
            return false;
          }
          // If payment creator status is accepted, prevent creator from editing
          if (response.data.status === "ACCEPTED" && isCurrentUserCreator) {
            return true;
          }
        }

        // Check if user is an auditor role
        if (userData.role === "applicationsAuditor") {
          // If no auditor assigned yet, allow any auditor to edit
          if (response.data.auditorId === null) {
            return false; // Allow editing
          }
          // If auditor is assigned, only allow that specific auditor to edit
          if (!isCurrentUserAuditor) {
            return true; // Prevent editing - not the assigned auditor
          }
          // Auditor can edit even if payment is accepted
          return false; // Allow editing
        }

        // Check if user is a payment executor role
        if (userData.role === "paymentExecutor") {
          // If no executor assigned yet, allow any executor to edit
          if (response.data.executorId === null) {
            return true; // prevent editing
          }
          // If executor is assigned, only allow that specific executor to edit
          if (isCurrentUserExecutor) {
            return false; // Allow editing
          }
          // Not the assigned executor - prevent editing
          return true; // Prevent editing
        }

        // Default: prevent editing for other roles
        return true;
      });

      setPaymentDocs(response.data.MCIPaymentDocs);
    } catch (error) {
      console.log(error);
      setPaymentError(t("errorGettingInvoicePayment"));
    } finally {
      setLoadingPayment(false);
    }
  };

  async function getInvoiceDocs() {
    try {
      setIsFetchingInvoiceFiles(true);
      // setError("");

      const response = await axiosClient.get(
        `/multiContainerInvoiceWithoutC/invoiceMainFiles/${id}`,
      );
      setInvoiceDocs(
        response.data?.data?.documents.filter(
          (file) => file.type !== "BaghdadTransactionRequest",
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetchingInvoiceFiles(false);
    }
  }

  useEffect(() => {
    fetchPayment();
    getInvoiceDocs();
  }, []);

  useEffect(() => {
    if (isEditing) {
      setRejectionReason(paymentEditableFields.notes || "");
      setTransNum(payment?.transactionNumber || "");
    }
  }, [isEditing, paymentEditableFields.notes, payment?.transactionNumber]);

  const handleEditPayment = async () => {
    if (paymentEditableFields.currentUserStatus === undefined) {
      setUpdatingPaymentError((prev) => ({
        ...prev,
        selecteError: t("youMustChooseStatus"),
      }));
      return;
    }

    if (
      userRole === "paymentExecutor" &&
      paymentEditableFields.currentUserStatus === null
    ) {
      setUpdatingPaymentError((prev) => ({
        ...prev,
        selecteError: t("youMustChooseStatus"),
      }));
      return;
    }

    if (
      userRole === "paymentExecutor" &&
      (paymentEditableFields.currentUserStatus === "EXECUTED" ||
        paymentEditableFields.currentUserStatus === "ACCEPTED") &&
      !transNum.trim()
    ) {
      setUpdatingPaymentError((prev) => ({
        ...prev,
        rejectionReasonError: t("transactionNumberRequired"),
      }));

      setTimeout(() => {
        setUpdatingPaymentError((prev) => ({
          ...prev, // rejectionReasonError: "",
        }));
      }, 10);
      return;
    }

    // Combine selected reasons with custom notes
    const combinedNotes = [rejectionReason].filter(Boolean).join("\n");

    if (
      !combinedNotes.trim() &&
      paymentEditableFields.currentUserStatus === "REJECTED"
    ) {
      setUpdatingPaymentError((prev) => ({
        ...prev,
        rejectionReasonError: t("PleaseEnterRejectionReason"),
      }));
      return;
    }

    try {
      setSaveLoading(true);
      setUpdatingPayment(true);
      setUpdatingPaymentError({ selecteError: "", rejectionReasonError: "" });

      // Prepare the request body
      const requestBody = {
        status: paymentEditableFields.currentUserStatus,
        notes: combinedNotes,
        transactionNumber: transNum,
      };

      // Add transactionNumber only for executor completing the payment
      if (
        userRole === "paymentExecutor" &&
        (paymentEditableFields.currentUserStatus === "EXECUTED" ||
          paymentEditableFields.currentUserStatus === "ACCEPTED")
      ) {
        requestBody.transactionNumber = transNum;
        requestBody.status = "ACCEPTED";
      }

      const response = await axiosClient.patch(
        `/multiCInvoicePaymentDetails/updateStatus/${paymentId}`,
        requestBody,
      );

      setIsEditing(false);
      fetchPayment();
    } catch (error) {
      setUpdatingPaymentError(t("errorEditingInvoicePayment"));
    } finally {
      setUpdatingPayment(false);
      setSaveLoading(false);
    }
  };

  const handlePrintFilees = async () => {
    try {
      setLoadingFilesPrinting(true);

      const response = await axiosClient.get(
        `/multiCInvoicePaymentDetails/printMerged/${paymentId}`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = url;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          iframe.remove();
        }, 2000);
      };
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingFilesPrinting(false);
    }
  };
  const handleDownloadFiles = async () => {
    try {
      setLoadingFilesDownload(true);
      const link = document.createElement("a");
      const files = await axiosClient.get(
        `/multiCInvoicePaymentDetails/downloadZippedFiles/${paymentId}`,
        {
          responseType: "blob",
        },
      );
      const blob = new Blob([files.data], {
        type: "application/zip",
      });
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = "files.zip";
      link.click();
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingFilesDownload(false);
    }
  };

  const handleSendFilesByEmail = async () => {
    try {
      setLoadingFilesEmail(true);
      setFilesEmailSuccess("");
      setFilesEmailError("");

      const files = await axiosClient.get(
        `/multiCInvoicePaymentDetails/downloadZippedFiles/${paymentId}`,
        { responseType: "blob" },
      );

      const zipFile = new File([files.data], `payment-${paymentId}-files.zip`, {
        type: "application/zip",
      });

      const formDataObject = {
        files: zipFile,
        invoiceNumber:
          payment?.multiContainerInvoice?.invoiceNumber || paymentId,
        subject: `مستندات الفاتورة #${payment?.multiContainerInvoice?.invoiceNumber || paymentId}`,
        customMessage: "Attached are the payment files.",
        senderName: localStorage.getItem("username") || "",
        senderEmail:
          localStorage.getItem("email") ||
          localStorage.getItem("orgEmail") ||
          "",
        organizationName: localStorage.getItem("organizationName") || "",
        paymentId: paymentId,
        multiContainerInvoiceId: payment?.multiContainerInvoice?.id || null,
      };

      const formData = Object.entries(formDataObject).reduce(
        (fd, [key, value]) => {
          fd.append(key, value);
          return fd;
        },
        new FormData(),
      );

      const response = await axiosClient.post("/sendPDF/email", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        setShowSendToPolicySureModal(false);
        setFilesEmailSuccess(
          `${t("filesSentSuccessfully")} ${t("to")} ${response?.data?.sentTo || "email"}.`,
        );
      }
    } catch (error) {
      console.error(error);
      setFilesEmailError(t("errorSendingFiles"));
    } finally {
      setLoadingFilesEmail(false);
    }
  };

  const handleExecutePayment = async () => {
    try {
      setExecuteLoading(true);
      const isCurrentUserExecutor =
        payment.executorId === parseInt(userData.userId);

      if (isCurrentUserExecutor) {
        // Leave payment - set executor status back to NOT_EXECUTED
        await axiosClient.patch(
          `/multiCInvoicePaymentDetails/updateStatus/${paymentId}`,
          {
            status: "NOT_EXECUTED",
            notes: "",
          },
        );
      } else {
        // Execute payment - set executor status to IN_PROGRESS
        await axiosClient.patch(
          `/multiCInvoicePaymentDetails/updateStatus/${paymentId}`,
          {
            status: "PENDING",
            notes: "",
          },
        );
      }

      fetchPayment();
    } catch (error) {
      console.error(error);
    } finally {
      setExecuteLoading(false);
    }
  };

  const canEditPayment =
    payment.status !== "sent" && payment.status !== "in-progress";

  if (loadingPayment) {
    return (
      <PageWrapper title={t("paymentDetails")} withBackButton>
        <Loader size={LOADER_SIZES.LARGE} />
      </PageWrapper>
    );
  }

  if (paymentError) {
    return (
      <PageWrapper title={t("paymentDetails")} withBackButton>
        <ErrorBox>{t(paymentError)}</ErrorBox>
      </PageWrapper>
    );
  }

  const invoiceFiles = sortByCustomOrder(
    invoiceDocs,
    invoiceOrderFiles,
    "type",
  );
  const bankFiles = sortByCustomOrder(paymentDocs, orderedKeys, "type");
  const currency = getCurrency(payment);

  const handleMarkAsSentToPolicies = async () => {
    try {
      await axiosClient.patch(
        `/multiCInvoicePaymentDetails/updateStatus/${paymentId}`,
        {
          status: "SENT_TO_POLICIES",
        },
      );

      fetchPayment();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <PageWrapper withBackButton>
      <h3>{t("paymentDetails")}</h3>

      <div className="o-row o-row--gap-size_100 t-bold_bold u-margin--top_400 o-row--vertical-align_center o-row--horizontal-align_space-between">
        <div>
          <span>
            {userData?.userType === "organization"
              ? `${t("organization")}:`
              : `${t("user")}:`}
          </span>
          <span>{`${localStorage.getItem("username")}`}</span>
        </div>

        <div>
          {userRole === "paymentExecutor" && (
            <CustomButton
              variant="blank"
              onClick={handleExecutePayment}
              title={
                payment.executorId === null
                  ? t("orderExecution")
                  : payment.executorId === parseInt(userData.userId)
                    ? t("cancelOrderExecution")
                    : t("orderExecution")
              }
              icon={
                payment.executorId === null
                  ? "orderExecution"
                  : payment.executorId === parseInt(userData.userId)
                    ? "cancelOrderExecution"
                    : "orderExecution"
              }
              disabled={
                (payment.executorId !== null &&
                  payment.executorId !== parseInt(userData.userId)) ||
                payment.executorStatus === "EXECUTED" ||
                payment?.auditorStatus !== "ACCEPTED" ||
                payment?.auditorId === null
              }
              iconSize={20}
            />
          )}
        </div>
      </div>

      <div className="u-margin--top_400">
        {payment?.transactionNumber !== null ? (
          <TextDiv
            title="transactionNum"
            value={payment?.transactionNumber}
            divClasses="o-row"
            pClasses="t-bold_bold u-min-width-specific_200"
          />
        ) : (
          ""
        )}
      </div>

      <SectionDivider title={t("creatorAndAuditor")} />
      <div className="o-column o-column--gap-size_600 u-margin_0">
        <div className="o-column o-column--gap-size_200">
          <TextDiv
            title="creator"
            value={payment?.creator?.username}
            divClasses="o-row "
            pClasses="t-bold_semi-bold u-min-width-specific_200"
          />

          <div className="o-row o-row--vertical-align_center">
            <label className="u-min-width-specific_200">
              {t("completionStatus")}:
            </label>
            {payment?.creatorStatus === "NOT_STARTED" ? (
              <StatusBadge color={"gray"} title={"notStarted"} />
            ) : payment.creatorStatus === "PENDING" ? (
              <StatusBadge color={"blue"} title={"inProgress"} />
            ) : payment.creatorStatus === "REJECTED" ? (
              <StatusBadge color={"red"} title={"declined"} />
            ) : payment.creatorStatus === "ACCEPTED" ? (
              <StatusBadge color={"green"} title={"approved"} />
            ) : null}
          </div>
        </div>

        <div className="o-column o-column--gap-size_200">
          <TextDiv
            title="auditor"
            value={payment?.auditor?.username}
            divClasses="o-row"
            pClasses="t-bold_semi-bold u-min-width-specific_200"
          />

          <div className="o-row o-row--vertical-align_center">
            <label className="u-min-width-specific_200">
              {t("completionStatus")}:
            </label>

            {payment?.auditorStatus === "NOT_STARTED" ? (
              <StatusBadge color={"gray"} title={"notStarted"} />
            ) : payment?.auditorStatus === "PENDING" ? (
              <StatusBadge color={"blue"} title={"inProgress"} />
            ) : payment?.auditorStatus === "REJECTED" ? (
              <StatusBadge color={"red"} title={"declined"} />
            ) : payment?.auditorStatus === "ACCEPTED" ? (
              <StatusBadge color={"green"} title={"approved"} />
            ) : null}
          </div>
        </div>

        <div className="o-column o-column--gap-size_200">
          <TextDiv
            title="executer"
            value={payment?.executor?.username}
            divClasses="o-row"
            pClasses="t-bold_semi-bold u-min-width-specific_200"
          />

          <div className="o-row o-row--vertical-align_center">
            <label className="u-min-width-specific_200">
              {t("excutionStatus")}:
            </label>

            {payment?.executorStatus === "NOT_EXECUTED" ? (
              <StatusBadge color={"gray"} title={"notStarted"} />
            ) : payment?.executorStatus === "PENDING" ? (
              <StatusBadge color={"blue"} title={"inProgress"} />
            ) : payment?.executorStatus === "REJECTED" ? (
              <StatusBadge color={"red"} title={"declined"} />
            ) : payment?.executorStatus === "EXECUTED" ? (
              <StatusBadge color={"green"} title={"executed"} />
            ) : null}
          </div>
        </div>
      </div>
      <div className="">
        <SectionDivider title={t("senderAndReceiver")} />
        <div className="o-column o-column--gap-size_300">
          <TextDiv
            title="sender"
            value={
              currentDirection === "rtl"
                ? payment?.multiContainerInvoice?.subCompany?.nameInAr
                : payment?.multiContainerInvoice?.subCompany?.name
            }
            divClasses="o-column-row-switcher"
            pClasses="t-bold_semi-bold u-min-width-specific_200"
          />
          <TextDiv
            title="receiver"
            value={payment?.multiContainerInvoice?.supplier?.name}
            divClasses="o-column-row-switcher"
            pClasses="t-bold_semi-bold u-min-width-specific_200"
          />
        </div>
      </div>
      <SectionDivider title={t("paymentDetails")} />
      <div className="u-margin--top_500 o-column o-column--gap-size_500">
        <div className="o-row o-row--vertical-align_center">
          <div className="u-min-width-specific_200">{t("invoiceAmount")}:</div>
          <span className="u-width_full-width t-bold_bold">
            {payment?.multiContainerInvoice?.amountForSupplier} {currency}
          </span>
        </div>
        <div className="o-row o-row--vertical-align_center">
          <div className="u-min-width-specific_200">{t("previousPaid")}:</div>
          <span className="u-width_full-width t-bold_bold">
            {payment?.multiContainerInvoice?.previouslyPaid} {currency}
          </span>
        </div>

        <div className="o-row o-row--vertical-align_center">
          <div className="u-min-width-specific_200">{t("currentPaid")}:</div>
          <span className="u-width_full-width t-bold_bold">
            {payment?.amount?.toFixed(4)} {currency}
          </span>
        </div>
        <div className="o-row o-row--vertical-align_center">
          <div className="u-min-width-specific_200">{t("paidPercentage")}:</div>
          <span className="u-width_full-width t-bold_bold">
            {Math.round(
              (payment?.amount /
                payment?.multiContainerInvoice?.amountForSupplier) *
                100,
              4,
            )}
            %
          </span>
        </div>

        <div className="o-row o-row--vertical-align_center">
          <div className="u-min-width-specific_200">{t("paymentDate")}:</div>
          <span className="u-width_full-width t-bold_bold">
            {formatDate(payment.date)}
          </span>
        </div>
      </div>
      <div className="u-margin--top_700" />

      <SectionDivider title={t("invoiceAndReceverDocuments")} />
      {invoiceFiles?.length === 0 && (
        <InformationBox>{t("noDocuments")}</InformationBox>
      )}

      {invoiceFiles.length > 0 && (
        <div className="o-grid o-grid--auto-columns_xsmall o-grid--gap-size_700">
          {invoiceFiles.map((doc) => (
            <PDFViewer
              key={doc?.id}
              path={`${process.env.REACT_APP_URL}/multiContainerInvoiceWithoutC/getInvoiceFile/${doc?.id}`}
              filename={filesTitles[doc?.type]}
              fileType={getFileType(doc?.filePath)}
              downloadable
            />
          ))}
        </div>
      )}
      <div className="u-margin--top_700" />
      <SectionDivider title={t("bankDocuments")} />
      {bankFiles?.length === 0 && (
        <InformationBox>{t("noDocuments")}</InformationBox>
      )}
      {bankFiles.length > 0 && (
        <div className="o-grid o-grid--auto-columns_xsmall o-grid--gap-size_700">
          {bankFiles.map((doc) => (
            <PDFViewer
              key={doc?.id}
              path={`${process.env.REACT_APP_URL}/multiCInvoicePaymentDetails/invoicePayment/docs/${doc?.id}`}
              filename={filesTitles[doc?.type]}
              fileType={getFileType(doc?.filePath)}
            />
          ))}
        </div>
      )}

      <section
        className={`u-margin--top_700 ${updatingPaymentError.rejectionReasonError ? "shake-animation-outline" : ""}`}
      >
        {isEditing ? (
          <>
            {(userRole === "paymentExecutor" &&
              paymentEditableFields.currentUserStatus === "ACCEPTED") ||
            paymentEditableFields.currentUserStatus === "EXECUTED" ? (
              <div className="u-width--max-width_500">
                <label className="t-bold_bold">{t("transactionNum")}:</label>
                <CustomInput
                  type="text"
                  value={transNum}
                  onChange={(e) => setTransNum(e.target.value)}
                />
              </div>
            ) : (
              <>
                {userRole === "paymentExecutor" && (
                  <div className="u-width--max-width_500 u-margin--bottom_500">
                    <label className="t-bold_bold">
                      {t("transactionNum")}:
                    </label>
                    <CustomInput
                      type="text"
                      value={transNum}
                      onChange={(e) => setTransNum(e.target.value)}
                    />
                  </div>
                )}

                <CommonRejectionsReasons
                  selectedReasons={selectedReasons}
                  setSelectedReasons={setSelectedReasons}
                  onRejectionReasonChange={setRejectionReason}
                  currentRejectionReason={paymentEditableFields.notes}
                />

                <label className="t-bold_bold">{t("notes")}:</label>
                <CustomInput
                  type="textarea"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
              </>
            )}
          </>
        ) : (
          <div>
            <label className="t-bold_bold">{t("notes")}:</label>
            <p style={{ whiteSpace: "pre-line" }}>
              {paymentEditableFields.notes}
            </p>
          </div>
        )}
      </section>

      {isEditing && (
        <div
          className={`o-column o-column--gap-size_400 u-margin--top_400 ${updatingPaymentError.selecteError ? "shake-animation-outline" : ""} u-width_fit-content`}
        >
          <label className="t-bold_bold">{t("status")}:</label>
          <RadioGroup>
            <RadioGroupItem>
              <Radio
                id="option1"
                value={"ACCEPTED"}
                checked={
                  paymentEditableFields.currentUserStatus === "ACCEPTED" ||
                  paymentEditableFields.currentUserStatus === "EXECUTED"
                }
                onChange={(e) =>
                  setPaymentEditableFields({
                    ...paymentEditableFields,
                    currentUserStatus: e.target.value,
                  })
                }
              />
              <RadioLabel htmlFor="option1">{t("completed")}</RadioLabel>
            </RadioGroupItem>

            <RadioGroupItem>
              <Radio
                id="option2"
                value="REJECTED"
                checked={paymentEditableFields.currentUserStatus === "REJECTED"}
                onChange={(e) =>
                  setPaymentEditableFields({
                    ...paymentEditableFields,
                    currentUserStatus: e.target.value,
                  })
                }
              />
              <RadioLabel htmlFor="option2">{t("incomplete")}</RadioLabel>
            </RadioGroupItem>
          </RadioGroup>
        </div>
      )}

      {analyzeLoading && <LoaderWithProgress />}

      {updatingPaymentError.rejectionReasonError && (
        <ErrorMessage>
          {t(updatingPaymentError.rejectionReasonError)}
        </ErrorMessage>
      )}
      {filesEmailError ? <ErrorBox>{filesEmailError}</ErrorBox> : null}
      {filesEmailSuccess ? (
        <ConfirmationBox>
          <p>{filesEmailSuccess}</p>
        </ConfirmationBox>
      ) : null}

      {saveLoading && <Loader />}

      <div className="o-row o-row--gap-size_200 o-row--horizontal-align_end">
        {updatingPaymentError.selecteError && (
          <ErrorMessage>
            <p>{t(updatingPaymentError.selecteError)}</p>
          </ErrorMessage>
        )}
      </div>
      <div className="o-row o-row--gap-size_200 o-row--horizontal-align_end u-padding--bottom_500 ">
        {isEditing && (
          <div className="o-row o-row--gap-size_200 o-row--horizontal-align_end">
            <CustomButton
              variant="blank"
              title={t("cancel")}
              onClick={() => {
                setIsEditing(false);
                setPaymentEditableFields({ ...payment });
                setUpdatingPaymentError({
                  selecteError: "",
                  rejectionReasonError: "",
                });
              }}
              icon="close"
              iconSize={15}
              disabled={saveLoading}
            />

            <CustomButton
              title={t("save")}
              icon="save"
              onClick={() => {
                handleEditPayment();
              }}
              iconSize={18}
              disabled={saveLoading}
            />
          </div>
        )}

        {!isEditing && canEditPayment && (
          <>
            {preventEditing ? null : (
              <div className="o-column o-column--gap-size_300">
                <div className="o-row o-row--gap-size_300">
                  <CustomButton
                    title={t("edit")}
                    variant="blank"
                    icon="edit"
                    disabled={analyzeLoading}
                    onClick={() => {
                      setIsEditing(true);
                    }}
                    iconSize={18}
                  />

                  <CustomButton
                    title={t("downloadFiles")}
                    variant={"blank"}
                    icon="download"
                    iconSize={25}
                    onClick={() => {
                      handleDownloadFiles();
                    }}
                    disabled={loadingFilesDownload}
                    loading={loadingFilesDownload}
                  />

                  <CustomButton
                    title={t("print")}
                    onClick={() => {
                      handlePrintFilees();
                    }}
                    variant="gray"
                    icon="print"
                    iconSize={18}
                  />
                </div>
                <div className="o-row o-row--gap-size_300 u-width_full-width">
                  <CustomButton
                    title={t("sendFilesToPoliciesDept")}
                    variant={"blank"}
                    onClick={() => setShowSendToPolicySureModal(true)}
                    disabled={loadingFilesEmail}
                    loading={loadingFilesEmail}
                    icon="sendEmail"
                    iconSize={24}
                    noMaxWidth
                  />
                </div>
                <div className="o-row o-row--gap-size_300">
                  <CustomButton
                    title={t("markAsSentToPolicies")}
                    variant="blank"
                    // onClick={handleMarkAsSentToPolicies}
                    onClick={() => setMarkAsSentToPolicies(true)}
                    icon="changeBadge"
                    iconSize={18}
                    noMaxWidth
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {showSendToPolicySureModal && (
        <AreYouSureModal
          confirmTitle={"send"}
          closeTitle={"cancel"}
          confirmIconType="whiteCheck"
          customMessage={"sureWantToSendToPoliciesDept"}
          confirm
          onConfirm={() => {
            handleSendFilesByEmail();
          }}
          onClose={() => {
            setShowSendToPolicySureModal(false);
          }}
          loading={loadingFilesEmail}
        />
      )}

      {showMarkAsSentToPolicies && (
        <AreYouSureModal
          confirmTitle={"send"}
          closeTitle={"cancel"}
          confirmIconType="whiteCheck"
          customMessage={"areYouSureYouWantToMarkAsSentToPolicies"}
          confirm
          onConfirm={() => {
            handleMarkAsSentToPolicies();
            setMarkAsSentToPolicies(false);
          }}
          onClose={() => {
            setMarkAsSentToPolicies(false);
          }}
        />
      )}
    </PageWrapper>
  );
};

export default BanksInvoicesApplicationPaymentDetails;
